import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuDataType } from '../../../home/Home';
import {
  Card,
  useBreakpoint,
  HeaderStyle,
  Tag,
  TagStyle,
  Theme,
  Tooltip,
  TooltipPreference,
} from '../../../../ui/components';
import { formatDateDMYYYY, dateTimeToCustomFormat, UTCToLocal, LoggerService } from '../../../../ui/utils';
import Personalize from '../../../../assets/image/personalize.png';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { DynamicObjectInterface } from '../../../../sdk/type';
import CoachService from '../../../../sdk/services/coachService';
import { useAPICall } from '../../../../hooks';
import { TeamList } from '../../team-list/list';
import { UserData } from '../../dashboardConfig';
import healthGoalService from '../../../../sdk/services/healthGoalService';
import AttachmentService from '../../../../sdk/services/attachmentService';
import styles from './index.module.scss';

interface CoachDetailsPropsType {
  index: number;
  decodedData: DynamicObjectInterface;
  dataCampaign: MenuDataType[];
  coachEnrollmentId: string;
}

const CoachDetails = (props: CoachDetailsPropsType) => {
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const { t } = useTranslation();
  const [data, setData] = useState<UserData | null>(null);
  const apiCall = useAPICall();

  const fetchData = async () => {
    try {
      let res = await apiCall(CoachService?.getCoachDetailByCampaignId, props?.coachEnrollmentId);

      let coachData = res?.data?.data?.[0];

      const profileImageRes = await AttachmentService.downloadProfileImage(coachData?.loginId ?? '');

      const healthGoalRes = await healthGoalService.userAnswer({
        userId: coachData?.loginId,
        campaignId: props.dataCampaign[props.index].campaignId,
      });

      setData((i: DynamicObjectInterface) => {
        i = coachData;
        i.activities = [];
        i.coachId = coachData?.coachId;
        i.campaignName = coachData?.campaignName;
        i.coachEnrollmentDate = coachData?.enrollmentDate;
        i.coachEndDate = coachData?.endDate;
        i.directLeaderName = coachData?.directLeaderName;
        i.directLeader = coachData?.directLeader;

        i.cumulativePoint = coachData?.cumulativePoint;
        i.newAboCount = coachData?.newAboCount;
        i.newGroupAboCount = coachData?.newGroupAboCount;
        coachData?.rewards?.filter((i: DynamicObjectInterface) => i.rewardStatus === 'ACTIVE').length > 0 &&
          i.activities.push({
            activityName: coachData?.rewards?.[0]?.activityName,
            reward: coachData?.rewards?.filter((i: DynamicObjectInterface) => i.rewardStatus === 'ACTIVE'),
          });
        i.profileImageUrl = profileImageRes?.data?.data?.image ?? null;
        i.answerText = healthGoalRes?.data?.data?.answerText;
        return { ...i };
      });
    } catch (error) {
      LoggerService.log('An error occurred while fetching data:', error);
    }
  };

  useEffect(() => {
    if (props?.coachEnrollmentId) {
      fetchData();
    }
  }, [props]);

  const userDetail = (
    <Card containerHeight="100%">
      <div className={styles['details']}>
        <div className={styles['details-header']}>
          <div className={styles['profile-image-container']}>
            <img
              src={data?.profileImageUrl ? data?.profileImageUrl : Personalize}
              width={96}
              height={96}
              alt="personalize"
              className={styles['main-profile-image']}
            />
          </div>
          <h2 className={styles['details-info-header']}>{t('admin.dashboard.coachProfile')}</h2>
        </div>
        <div className={styles['details-info-top']}>
          <div className={styles['details-info-wrapper']}>
            <div className={styles['label']}>{t('admin.dashboard.name')}</div>
            <div>{data?.name ?? '-'}</div>
          </div>
          <div className={styles['details-info-wrapper']}>
            <div className={styles['label']}>{t('admin.dashboard.aboId')}</div>
            <div>{data?.aboId ?? data?.loginId ?? '-'}</div>
          </div>
          <div className={styles['details-info-wrapper']}>
            <div className={styles['label']}>{t('admin.dashboard.contactNo')}</div>
            <div>{data?.phone ?? '-'}</div>
          </div>
          <div className={styles['details-info-wrapper']}>
            <div className={styles['label']}>{t('admin.dashboard.email')}</div>
            <div className={styles['email']}>{data?.email ?? '-'}</div>
          </div>
        </div>

        <div className={styles['details-info-bottom']}>
          <div className={`${styles['details-info-item']} ${styles['details-info-bottom-item-left']}`}>
            <div className={styles['label']}>Role</div>
            <p>Team Coach</p>
          </div>
          {!isDesktopView && <div className={styles['progress-monitoring-horizontal-line']} />}
          <div className={styles['details-info-bottom-item-right']}>
            <div className={styles['details-info']}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.dashboard.coachId')}</div>
                <p>{data?.coachId ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.dashboard.coachDeclarePeriod')}</div>
                <p>
                  {data?.coachEnrollmentDate
                    ? dateTimeToCustomFormat(data?.coachEnrollmentDate?.toString() ?? '', 'MMM YYYY') +
                      ' - ' +
                      dateTimeToCustomFormat(data?.coachEndDate?.toString() ?? '', 'MMM YYYY')
                    : '-'}
                </p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.dashboard.directLeaderName')}</div>
                <p>{data?.directLeaderName ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.dashboard.directLeader')}</div>
                <p>{data?.directLeader ?? '-'}</p>
              </div>
            </div>
          </div>
        </div>
        {data?.answerText !== undefined && data?.answerText !== null && (
          <div className={styles['progress-monitoring-horizontal-line']} />
        )}
        {/* health goal section  */}
        {data?.answerText !== undefined && data?.answerText !== null && (
          <div>
            <div className={styles['progress-monitoring-title-container']}>
              <div className={styles['details-info-header-healthGoal']}>Health Goal</div>
              <div className={styles['progress-monitoring-title-container-subtitle']}>(Optional)</div>
              <Tooltip
                message={
                  'Health goals are optional and are intended for your personal records to help you stay focused throughout the journey.'
                }
                preference={TooltipPreference.RIGHT}
                className={styles['input-tool-tip']}
              />
            </div>
            <div className={styles['health-goal-section']}>
              <div className={styles['health-goal-content']}>{data?.answerText}</div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  const resultDetail = (
    <>
      <Card
        title={t('admin.dashboard.achievements')}
        headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      >
        <div className={`${styles['details-info-result']} ${styles['details-info-result-flex']}`}>
          <div className={styles['details-info-result-total']}>
            <div className={styles['details-info-result-total-title']}>Completed Journey</div>
            <div className={styles['details-info-result-total-number']}>{data?.achievement?.completed}</div>
          </div>
          {/* <div className={styles['details-info-result-total']}>
            <div className={styles['details-info-result-total-title']}>
              {t('Subscribed to SOP')}
              <span className={styles['details-info-result-total-note-mobile']}>
                Note: This does not contribute to the coach/leader winning criteria.
              </span>
            </div>
            <div className={styles['details-info-result-total-number']}>{data?.achievement?.alp ?? 0}</div>
            <span className={styles['details-info-result-total-note-desktop']}>
              Note: This does not contribute to the coach/leader winning criteria.
            </span>
          </div> */}
          <div className={styles['details-info-result-total']}>
            <div className={styles['details-info-result-total-title']}>Achieved Individual Criteria</div>
            <div className={styles['details-info-result-total-number']}>{data?.achievement?.achieved}</div>
          </div>
          <div className={styles['details-info-result-total']}>
            <div className={styles['details-info-result-total-title']}>
              New Personal Sponsored ABO/APC
              <span className={styles['details-info-result-total-note-mobile']}>
                Last Update:{' '}
                {data?.newAboCount?.lastUpdateDate
                  ? `${formatDateDMYYYY(UTCToLocal(data?.newAboCount?.lastUpdateDate))}`
                  : '-'}
              </span>
            </div>
            <div className={styles['details-info-result-total-number']}>{data?.achievement?.sponsor}</div>
            <span className={styles['details-info-result-total-note-desktop']}>
              Last Update:{' '}
              {data?.newAboCount?.lastUpdateDate
                ? `${formatDateDMYYYY(UTCToLocal(data?.newAboCount?.lastUpdateDate))}`
                : '-'}
            </span>
          </div>
        </div>
      </Card>
    </>
  );

  const rewardGiftModal = data?.activities?.map((activity, index) => {
    const hasReward = activity?.reward?.length > 0;

    return (
      hasReward && (
        <div key={index} className={styles['details-info-reward-list']}>
          {data?.activities?.map((activity, index) => (
            <div key={index}>
              <div className={styles['details-info-reward-title']}>{activity?.activityName}</div>
              <div>
                {activity?.reward?.map((reward, rewardIndex) => (
                  <Fragment>
                    <div className={styles['details-info-reward-item']} key={rewardIndex}>
                      <div className={styles['details-info-reward-label']}>{reward?.rewardName}</div>
                      <span className={styles['details-info-reward-value']}>
                        <Tag
                          theme={reward?.redeemDate ? Theme.GRAY : Theme.SUCCESS}
                          style={TagStyle.OUTLINE}
                          text={
                            reward?.redeemDate
                              ? `REDEEMED (${formatDateDMYYYY(new Date(reward?.redeemDate))})`
                              : 'ACTIVE'
                          }
                        />
                      </span>
                    </div>
                    {rewardIndex !== activity?.reward?.length - 1 && <div className={styles['horizontal-divider']} />}
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    );
  });

  const rewardDetail = (
    <Card
      title={'Reward(s)'}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      containerHeight="100%"
    >
      {data?.activities && data?.activities?.length > 0 ? (
        <>{rewardGiftModal}</>
      ) : (
        <OperationCenterErrorCard
          errorType={ErrorType.NO_REWARD_AVAILABLE}
          className={styles['team-details-opt-card']}
        />
      )}
    </Card>
  );

  return (
    <div className={styles['dashboard-container']}>
      {userDetail}
      <div className={styles['dashboard-container-layout']}>
        <div className={styles['dashboard-container-layout-left']}>{resultDetail}</div>
        {data?.activities && data?.activities?.length > 0 && (
          <div className={styles['dashboard-container-layout-right']}>{rewardDetail}</div>
        )}
      </div>
      <div className={styles['dashboard-container-layout-bottom']}>
        {/* need to get from coach api only */}
        {data?.coachId && (
          <Card
            title={'Team Progress'}
            headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
            // containerHeight="100%"
          >
            <TeamList
              decodedData={props?.decodedData}
              index={props?.index}
              campaignData={props?.dataCampaign}
              callFrom={'coachList'}
              coachId={data?.coachId}
              enrollmentId={props.coachEnrollmentId}
              enrollmentStartDate={data?.coachEnrollmentDate}
              enrollmentEndDate={data?.coachEndDate}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default CoachDetails;
